import styled, { css, keyframes } from 'styled-components';
import { down } from 'styled-breakpoints';

import { TransientProps } from '@common/types';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const sway = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(0.8deg); }
  75% { transform: rotate(-0.8deg); }
`;

const wave = keyframes`
  0% { transform: scaleX(1) scaleY(1); opacity: 0.4; }
  100% { transform: scaleX(1.5) scaleY(2.2); opacity: 0; }
`;

export const Card = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[100]};
    border-radius: 1rem;
    border: 1px solid ${theme.colors.background[40]};
    margin-bottom: 2.95rem;
    padding: 1.375rem 1.5rem;

    &::after {
      content: '';
      display: table;
      clear: both;
    }

    ${down('lg')} {
      padding: 1rem;
    }
  `}
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 0.5rem;

  > h1 {
    font-size: 1.125rem;
    font-weight: 700;
  }
`;

export const MainStageWrapper = styled.div`
  float: right;
  width: 27.5rem;
  height: 14.375rem;
  position: relative;
  z-index: 2;
  margin: 0 0 0.75rem 1.25rem;
  border-radius: 1.25rem;
  overflow: hidden;
  background: linear-gradient(135deg, #0f172a, #1e293b);
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(15, 22, 40, 0.25);
  }

  ${down('lg')} {
    float: none;
    width: 100%;
    height: 12rem;
    margin: 0 0 1rem 0;
  }
`;

export const MainStageImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

type StatusVariant = 'live' | 'soon' | 'reprise';

const statusColors: Record<StatusVariant, { color: string; bg: string }> = {
  live: { color: '#dc2626', bg: 'rgba(220, 38, 38, 0.12)' },
  soon: { color: '#e5a00d', bg: 'rgba(229, 160, 13, 0.12)' },
  reprise: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.12)' },
};

export const StatusTag = styled.div<{ $variant: StatusVariant }>`
  ${({ $variant }) => css`
    position: absolute;
    top: 0.875rem;
    left: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.3125rem 0.75rem;
    border-radius: 0.5rem;
    background: ${statusColors[$variant].bg};
    backdrop-filter: blur(8px);
    color: ${statusColors[$variant].color};
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    z-index: 3;
  `}
`;

export const LivePulse = styled.span`
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 50%;
  background: #dc2626;
  animation: ${pulse} 1.5s infinite;
`;

export const PlayCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.85);
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 3;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  ${MainStageWrapper}:hover & {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

export const GlassBar = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 0.75rem;
  right: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 3;
  transform-origin: center bottom;
  animation: ${sway} 4s ease-in-out infinite;
  transition: background 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  ${MainStageWrapper}:hover & {
    background: rgba(255, 255, 255, 0.14);
  }
`;

export const GlassPlayIcon = styled.div`
  width: 2.375rem;
  height: 2.375rem;
  border-radius: 0.5rem;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1b2a4a;
  flex-shrink: 0;
`;

export const GlassBarInfo = styled.div`
  flex: 1;
`;

export const GlassBarName = styled.div`
  ${({ theme }) => css`
    font-size: 0.9375rem;
    font-weight: 700;
    color: ${theme.colors.thumbnail.text};
    margin-bottom: 0.125rem;
  `}
`;

export const GlassBarSub = styled.div`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    color: color-mix(in srgb, ${theme.colors.thumbnail.text} 50%, transparent);
  `}
`;

export const GlassEnterBtn = styled.button`
  position: relative;
  padding: 0.5rem 1.25rem;
  border-radius: 0.5rem;
  border: none;
  background: white;
  color: #1b2a4a;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 0.5rem;
    background: rgba(255, 255, 255, 0.3);
    animation: ${wave} 2.5s ease-out infinite;
  }

  &::after {
    animation-delay: 0.8s;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.brand.primary.default};
    color: ${({ theme }) => theme.colors.brand.primary.text};
  }
`;

// Legacy styles kept for description area

type DescriptionProps = TransientProps<{ expanded: boolean }>;

export const Description = styled.div<DescriptionProps>`
  ${({ $expanded }) => css`
    position: relative;

    > p {
      font-size: 0.8125rem;
      color: var(--colors-foreground-60);
      line-height: 1.7;
      max-height: ${$expanded ? 'none' : '11rem'};
      overflow: ${$expanded ? 'visible' : 'hidden'};
      transition: max-height 0.4s ease;
    }
  `}
`;

export const DescriptionFade = styled.div`
  ${({ theme }) => css`
    position: absolute;
    bottom: 0;
    left: 0;
    width: calc(100% - 28rem);
    height: 3.5rem;
    background: linear-gradient(transparent, ${theme.colors.background[100]});
    pointer-events: none;

    ${down('lg')} {
      width: 100%;
    }
  `}
`;

export const ReadMoreButton = styled.span`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    color: ${theme.colors.brand.primary.default};
    font-weight: 600;
    cursor: pointer;
    margin-top: 0.125rem;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;

    > svg {
      transition: transform 0.3s ease;
    }
  `}
`;
